import { useMemo, useState } from "react";
import PageShell from "@/components/feature/PageShell";
import CategoryPage from "@/components/feature/CategoryPage";
import FilterGroup from "@/components/base/FilterGroup";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import CharacterCard from "@/components/feature/CharacterCard";
import { useSeo } from "@/hooks/useSeo";
import { charactersList, characterArchetypes } from "@/mocks/characters";

const PAGE_SIZE = 6;

export default function CharactersPage() {
  useSeo(
    "age动漫 - 角色分析专题",
    "age动漫角色分析专题页，从人物弧线、动机与叙事功能出发解析热门动漫角色，可按所属作品与角色类型筛选，深入理解角色的每个选择。",
    "age动漫角色分析,动漫角色解析,角色专题,人物弧线,二次元角色",
  );

  const [work, setWork] = useState("全部");
  const [archetype, setArchetype] = useState("全部");
  const [page, setPage] = useState(1);

  const workOptions = useMemo(() => {
    const map = new Map<string, string>();
    charactersList.forEach((item) => {
      map.set(item.workId, item.workTitle);
    });
    return [
      { label: "全部作品", value: "全部" },
      ...Array.from(map.entries()).map(([value, label]) => ({ label, value })),
    ];
  }, []);

  const filtered = useMemo(
    () =>
      charactersList.filter(
        (item) =>
          (work === "全部" || item.workId === work) &&
          (archetype === "全部" || item.archetype === archetype),
      ),
    [work, archetype],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const current = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setWork("全部");
    setArchetype("全部");
    setPage(1);
  };

  return (
    <PageShell>
      <CategoryPage
        breadcrumb={[{ label: "首页", to: "/" }, { label: "角色专题" }]}
        kicker="CHARACTERS"
        title="角色分析专题"
        description="角色的魅力从来不只是设定图上的几行文字。我们关注人物在故事里做过的选择、说过的台词，以及那些没有被明说的动机，试着把「喜欢」拆解成可以讨论的部分。"
        meta={
          <div className="flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-star-line text-primary-500"></i>
              {charactersList.length} 篇解析
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-primary-500"></i>
              每周更新
            </span>
          </div>
        }
        filterContent={
          <>
            <FilterGroup
              title="所属作品"
              options={workOptions}
              value={work}
              onChange={(value) => {
                setWork(value);
                setPage(1);
              }}
            />
            <FilterGroup
              title="角色类型"
              options={characterArchetypes.map((item) => ({ label: item, value: item }))}
              value={archetype}
              onChange={(value) => {
                setArchetype(value);
                setPage(1);
              }}
              grid
            />
            <div className="rounded-md bg-background-100 p-3">
              <p className="text-[11px] text-foreground-500 leading-relaxed">
                解析内容包含剧透，建议在观看对应作品后阅读。
              </p>
            </div>
          </>
        }
        count={filtered.length}
        onReset={reset}
      >
        {current.length === 0 ? (
          <EmptyState
            title="暂无相关角色专题"
            description="当前筛选条件下还没有解析内容，试试其它作品或角色类型。"
            icon="ri-user-star-line"
            action={
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {current.map((item) => (
                <CharacterCard key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-8">
              <Pagination
                currentPage={safePage}
                totalPages={totalPages}
                onChange={(next) => {
                  setPage(next);
                  window.scrollTo({ top: 240, behavior: "smooth" });
                }}
              />
            </div>
          </>
        )}
      </CategoryPage>
    </PageShell>
  );
}